import { useContext, useEffect, useMemo, useState } from "react";
import {
  CashOutAmount,
  EContractType,
  ESenderRole,
} from "../../../setup/api/services/contract/models";
import styles from "./styles.module.scss";
import { ContractContext } from "../../../providers/contract";
import UserCard from "./UserCard";
import { useTranslation } from "react-i18next";
import StatusPill from "./StatusPill";
import classNames from "classnames";
import PrimaryButton, {
  PrimaryButtonType,
} from "../../../components/common/PrimaryButton";
import NewCashoutModal from "../NewCashoutModal";
import { AlertColor } from "@mui/material";
import CustomizedSnackbars from "../../../components/common/customizedSnackbars";
import { useLocation } from "react-router-dom";
import { userMatchesIdentifier } from "../../../setup/util/userIdentifier";

interface ICashoutCard {
  cashOutAmount: CashOutAmount;
  disableCashout?: boolean;
  currentPage?: number;
  hideFees?: boolean;
}

const CashoutCard = ({
  cashOutAmount,
  disableCashout,
  hideFees,
}: ICashoutCard) => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);
  const location = useLocation();

  const [openCashoutModal, setOpenCashoutModal] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>("info");
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  // Check if isOperation is true in search params
  const isOperation = useMemo(() => {
    const searchParams = new URLSearchParams(location?.search);
    return searchParams?.get("isOperation") === "true";
  }, [location.search]);

  const partyUser = contract?.parties?.find((party) =>
    userMatchesIdentifier(party, cashOutAmount.userIdentification)
  );

  const user = useMemo(() => {
    if (contract?.type !== EContractType.QUICK_TRANSACTION) {
      return (
        contract?.partiesProfiles?.CUSTOMER.find((party) =>
          userMatchesIdentifier(party, cashOutAmount.userIdentification)
        ) ||
        contract?.partiesProfiles?.PROVIDER.find((party) =>
          userMatchesIdentifier(party, cashOutAmount.userIdentification)
        ) ||
        contract?.partiesProfiles?.BROKER.find((party) =>
          userMatchesIdentifier(party, cashOutAmount.userIdentification)
        )
      );
    }

    if (
      userMatchesIdentifier(
        contract?.customer,
        cashOutAmount.userIdentification
      )
    ) {
      return contract?.customer;
    } else if (
      userMatchesIdentifier(
        contract?.provider,
        cashOutAmount.userIdentification
      )
    ) {
      return contract?.provider;
    } else if (partyUser) {
      return partyUser;
    }
    return contract?.provider;
  }, [
    cashOutAmount.userIdentification,
    contract?.customer,
    contract?.partiesProfiles?.BROKER,
    contract?.partiesProfiles?.CUSTOMER,
    contract?.partiesProfiles?.PROVIDER,
    contract?.provider,
    contract?.type,
    partyUser,
  ]);

  const userType = useMemo(() => {
    if (partyUser) {
      return partyUser.role;
    }

    if (
      userMatchesIdentifier(
        contract?.customer,
        cashOutAmount.userIdentification
      )
    ) {
      return ESenderRole.CUSTOMER;
    }
    return ESenderRole.PROVIDER;
  }, [cashOutAmount.userIdentification, contract?.customer, partyUser]);

  const userTypeText = useMemo(() => {
    if (userType === ESenderRole.CUSTOMER) {
      return t("lbl_the_buyer");
    } else if (userType === ESenderRole.PROVIDER) {
      return t("lbl_the_seller");
    } else if (userType === ESenderRole.BROKER) {
      return t("lbl_brokers");
    } else if (userType === ESenderRole.SERVICE_PROVIDER) {
      return t("lbl_naqel");
    }
    return "";
  }, [t, userType]);

  const hasManualWithdraw = useMemo(() => {
    return !!cashOutAmount?.manualWithDrawCashOut;
  }, [cashOutAmount]);

  const hasPendingMultiCashOut = useMemo(() => {
    return !!cashOutAmount?.multiCashOut;
  }, [cashOutAmount]);

  const disableButton = useMemo(() => {
    return (
      disableCashout ||
      (hasManualWithdraw && contract?.type !== EContractType.QUICK_TRANSACTION)
    );
  }, [contract?.type, disableCashout, hasManualWithdraw]);

  const userLastTransaction = useMemo(() => {
    const userTransactions = contract?.transactions.filter((transaction) => {
      const matchesIdentifier = userMatchesIdentifier(
        user,
        transaction.beneficiaryId
      );

      if (userType === ESenderRole.CUSTOMER) {
        return (
          matchesIdentifier &&
          (transaction.type === "CASH_OUT" ||
            transaction.type === "REFUND_CUSTOMER")
        );
      }
      return (
        matchesIdentifier &&
        (transaction.type === "CASH_OUT" ||
          transaction.type === "RELEASE_TO_PROVIDER" ||
          transaction.type === "CLOSE_RELEASE_TO_PROVIDER")
      );
    });
    if (userTransactions?.length) {
      return userTransactions[userTransactions.length - 1];
    }
    return null;
  }, [contract, user, userType]);

  const onTransferButtonClick = () => {
    setOpenCashoutModal(true);
  };

  const onCloseCashoutModal = () => {
    setOpenCashoutModal(false);
  };

  const onCopy = async () => {
    try {
      await navigator?.clipboard?.writeText(userLastTransaction?.id || "");
      setSnackBarOpen(true);
      setSnackBarColor("success");
      setSnackBarTitle(t("COPIED"));
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBarColor("error");
      setSnackBarTitle(t("COPIED"));
      console.error(error);
    }
  };

  useEffect(() => {
    if (snackBarOpen) {
      setTimeout(() => {
        setSnackBarOpen(false);
        setSnackBarColor("info");
        setSnackBarTitle("");
      }, 5000);
    }
  }, [snackBarOpen]);

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  return (
    <div className={styles.card}>
      <div className={styles.container}>
        <div className={styles.content}>
          <UserCard
            name={`${user?.profile?.fullName || ""} ${
              contract?.type !== EContractType.QUICK_TRANSACTION ||
              !user?.profile?.fullName
                ? `(${userTypeText || ""})`
                : ""
            }`}
            type={
              contract?.type !== EContractType.QUICK_TRANSACTION ||
              !user?.profile?.fullName
                ? user?.phoneNumber || ""
                : userTypeText
            }
            image={user?.profile?.photoUrl || "/assets/images/user.png"}
            isNationalIdVerified={user?.nationalIdVerified}
            isDescriptionLtr
          />
          <StatusPill
            status={
              userLastTransaction ? userLastTransaction.status : "NOT_SENT"
            }
          />
        </div>

        <div className={styles.content} style={{ alignItems: "center" }}>
          <div className={styles.amount_container}>
            <div className={styles.amount}>
              <p className={styles.amount_title}>{t("lbl_deserved_amount")}</p>
              <p className={styles.amount_description}>
                {cashOutAmount.amountDue + " " + t("lbl_saudi_currency")}
              </p>
            </div>

            {!hideFees && (
              <>
                <div className={styles.amount}>
                  <p className={styles.amount_title}>{t("SERVICE_FEE")}</p>
                  <p className={styles.amount_description}>
                    {`${
                      cashOutAmount?.feePercentage
                        ? `${(
                            (cashOutAmount?.feePercentage || 0) * 100
                          ).toFixed(2)}% - `
                        : ""
                    } ${cashOutAmount.fee} ${t("lbl_saudi_currency")}`}
                  </p>
                </div>

                <div className={styles.amount}>
                  <p className={styles.amount_title}>
                    {t("lbl_loyal_receive")}
                  </p>
                  <p
                    className={classNames(
                      styles["received_amount"],
                      styles.amount_description
                    )}>
                    {cashOutAmount.cashOutAmount +
                      " " +
                      t("lbl_saudi_currency")}
                  </p>
                </div>
              </>
            )}

            {userLastTransaction && userLastTransaction.id && (
              <div className={styles.amount}>
                <p className={styles.amount_title}>
                  {t("lbl_reference_number")}
                </p>
                <div className={styles.reference_number}>
                  <p className={styles.amount_description}>
                    {userLastTransaction
                      ? userLastTransaction.id
                      : t("lbl_no_reference_number")}
                  </p>
                  <img
                    src="/assets/images/svg/copy.svg"
                    alt=""
                    onClick={onCopy}
                  />
                </div>
              </div>
            )}
          </div>

          {(!userLastTransaction ||
            (userLastTransaction && userLastTransaction.status === "FAILED")) &&
            !disableButton &&
            !isOperation && (
              <PrimaryButton
                style={{
                  maxWidth: "227px",
                  backgroundColor: hasManualWithdraw
                    ? "transparent"
                    : "#0C4BC4",
                  height: "44px",
                  border: "1px solid #6F707338",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  pointerEvents: hasManualWithdraw ? "none" : undefined,
                }}
                buttonType={
                  hasManualWithdraw
                    ? PrimaryButtonType.DISABLED
                    : PrimaryButtonType.DEFAULT
                }
                onClick={
                  !hasManualWithdraw ? onTransferButtonClick : undefined
                }>
                {hasManualWithdraw
                  ? t("lbl_manual_transfer")
                  : t("lbl_release")}
              </PrimaryButton>
            )}
          {hasPendingMultiCashOut && (
            <PrimaryButton
              style={{
                maxWidth: "227px",
                backgroundColor: "transparent",
                height: "44px",
                border: "1px solid #6F707338",
                fontSize: "0.875rem",
                fontWeight: 500,
                pointerEvents: "none",
              }}
              buttonType={PrimaryButtonType.DISABLED}>
              {t("lbl_pending_multi_cash_out")}
            </PrimaryButton>
          )}
        </div>
      </div>
      <NewCashoutModal
        open={openCashoutModal}
        onClose={onCloseCashoutModal}
        cashOutAmount={cashOutAmount}
        userType={userType || ESenderRole.CUSTOMER}
        user={user}
      />
      <div className={styles.snackbar}>{renderSnackBar()}</div>
    </div>
  );
};

export default CashoutCard;
